import { useFormattedNumber } from "../../Hooks/UseFormattedNumber";
import {  useEffect, useState } from "react";
import "../../../../../css/CashflowModeller2/Tabs/Notes.css";
import "../../../../../css/CashflowModeller2/Inputs/Inputs.css"; // Ensure you have the correct path to your CSS file

/**
 * InputTextArea is a customizable textarea input component with support for labels, 
 * character limits, formatting (number/currency), and error/warning states.
 *
 * @component
 * @param {Object} props - The props for InputTextArea.
 * @param {string} [props.label] - Optional label to display above the textarea.
 * @param {string|number} [props.value] - The current value of the textarea.
 * @param {function} [props.onChange] - Callback function called when the textarea value changes.
 * @param {string} [props.placeholder=""] - Placeholder text for the textarea.
 * @param {boolean} [props.isDisabled=false] - If true, disables the textarea.
 * @param {boolean} [props.readOnly=false] - If true, makes the textarea read-only.
 * @param {boolean} [props.isError=false] - If true, applies error styling to the textarea.
 * @param {number} [props.rows=4] - Number of visible text lines for the textarea.
 * @param {string} [props.type="text"] - Type of input ("text", "number", "customDate").
 * @param {boolean} [props.currency=false] - If true and type is "number", formats value as currency.
 * @param {number} [props.charLimit=500] - Maximum number of characters allowed in the textarea.
 * 
 * @returns {JSX.Element} The rendered textarea input component.
 */
const InputTextArea = ({
  label,
  value,
  onChange,
  placeholder = "",
  isDisabled = false,
  readOnly = false,
  isError = false,
  rows = 4,
  type = "text",
  currency = false,
  charLimit = 500, // Optional: max characters allowed
}) => {
  const [status, setStatus] = useState("default");
  const { rawValue, formattedValue, handleChange } = useFormattedNumber(
    value || "",
    type
  );
  const [internalValue, setInternalValue] = useState(formattedValue);

  useEffect(() => {
    setInternalValue(formattedValue);

  }, [formattedValue]);


  /**
   * Handles changes to the local input value, enforcing character limits and formatting for number types.
   *
   * - Prevents input if the value exceeds the specified character limit.
   * - If the input type is "number", formats the value using `handleChange`, updates the internal state,
   *   and calls `onChange` with only numeric characters and decimal points.
   * - For other types, updates the internal state and calls `onChange` with the raw value.
   *
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e - The change event from the input or textarea.
   */
  const handleLocalChange = (e) => {
    const val = e.target.value;

    if (val.length > charLimit) return;

    if (type === "number") {
      const formatted = handleChange(e);
      setInternalValue(formatted);
      onChange?.(val.replace(/[^0-9.]/g, ""));
    } else {
      setInternalValue(val);
      onChange?.(val);
    }
  };

  /**
   * Returns the display value for the input based on its type and formatting options.
   *
   * - If the input type is "text" or "customDate", returns the raw value.
   * - If there is no internal value, returns an empty string.
   * - If a currency flag is set, prefixes the internal value with the pound symbol (£).
   * - Otherwise, returns the internal value as is.
   *
   * @returns {string} The formatted display value for the input.
   */
  const getDisplayValue = () => {
    if (type === "text" || type === "customDate") return value;
    if (!internalValue) return "";
    return currency ? `£${internalValue}` : internalValue;
  };

  const currentLength = getDisplayValue().length || 0;
  const showWarning = charLimit && currentLength === charLimit && status === "selected";

  return (
    <div
      className={`input-label-container-textarea ${
        label ? "input-label-container" : ""
      }`}
    >
      {label && <div className="Label">{label}</div>}

      <div
        className={`input-container textarea ${
          isDisabled ? "disabled" : ""
        } ${status} ${readOnly ? "readOnly" : ""} ${isError ? "error" : ""}
          ${showWarning ? "warning" : ""}`}
        style={{
          alignItems: "flex-start",
        }}
      >
        <textarea
          value={getDisplayValue() || ""}
          onChange={handleLocalChange}
          onFocus={
            () => !readOnly && setStatus("selected")
            // Add any additional focus logic here
          }
          onBlur={() => setStatus("default")}
          className="input-field Input"
          placeholder={placeholder || "Notes..."}
          disabled={isDisabled}
          readOnly={readOnly}
          rows={rows}
          maxLength={charLimit}
        />
        <div className={`char-count ${showWarning ? "error" : ""}`}>
          <div className="Content-Small">{`${currentLength} / ${charLimit} characters`}</div>
        </div>
      </div>
    </div>
  );
};

export default InputTextArea;
